import {
  PILLAR_PAGE_MAP,
  PillarPage,
  SearchPageCity
} from 'src/constants/searchPages'

/**
 * Per-city "Desde $X/mes" floors, measured from live bookable inventory.
 *
 * **A city page shows a "Desde" price ONLY for its own floor recorded here.**
 * Everywhere else it shows no price — a satellite municipality does NOT
 * inherit its metro parent's floor, and a hood page makes no claim. `PILLAR_PAGE_MAP[pillar].startingPrice` is the NATIONAL
 * floor and appears only on surfaces with no city.
 *
 * Why (full marketing check, marketing/reports/2026-10-14): every city/hood
 * header used to print the national number, which was false almost
 * everywhere it was checked — Monterrey parking said "Desde $500" over a
 * $1,150 cheapest card, Monterrey storage "$599" over $850, Querétaro "$599"
 * over $3,660, and Tijuana/Toluca/Mérida "$599" with no mini-bodegas at all.
 * A national floor only proves that ONE city somewhere is that cheap.
 *
 * Re-measure before adding or changing a value. `get_city_overview` (SpotMe
 * MCP) reads only the FIRST 50 locations of a city, so in a big market its
 * `minPrice` can miss the floor (it said $20,640 for Monterrey bodegas
 * comerciales; the floor was $10,440) — confirm with `search_listings`
 * `maxPriceMxn` = floor − 1 returning nothing (that filter runs in ES over the
 * whole city). Promo prices count; "1er mes" teasers don't. Move that city's
 * Google Ads claims in the same change — mirror map in
 * docs/claude/price-claims.md.
 * A stale entry is a false claim again: re-check at every quarterly scan.
 *
 * Satellites used to inherit the metro parent's floor (`METRO_PARENT_MAP`):
 * the San Pedro storage page said "Desde $850" over a $2,299 cheapest card,
 * and San Nicolás said $850 with no mini-bodegas at all (bi-weekly review,
 * marketing/reports/2026-10-26). Record a satellite's own floor instead.
 */
const CITY_PRICE_FLOORS: Partial<
  Record<PillarPage, Partial<Record<SearchPageCity, string>>>
> = {
  // Measured 2026-10-14; satellites 2026-10-26.
  [PillarPage.SelfStorage]: {
    [SearchPageCity.Monterrey]: '$850', // 73 metro listings, 2.5 m² in Centro
    [SearchPageCity.Guadalupe]: '$1,100',
    [SearchPageCity.Apodaca]: '$1,133',
    [SearchPageCity.SantaCatarina]: '$1,240', // 2.5 m² in Parque 300
    [SearchPageCity.Escobedo]: '$1,260',
    [SearchPageCity.SanPedro]: '$2,299', // 5 m² in Del Valle
    [SearchPageCity.Zapopan]: '$750',
    [SearchPageCity.Tlajomulco]: '$1,700',
    [SearchPageCity.CiudadDeMexico]: '$599', // backed by a $563 promo card
    [SearchPageCity.Guadalajara]: '$750', // 2.5 m² in Zapopan
    [SearchPageCity.Queretaro]: '$3,660',
    [SearchPageCity.Puebla]: '$3,075',
    [SearchPageCity.Mexicali]: '$1,000',
    [SearchPageCity.Veracruz]: '$1,984',
    [SearchPageCity.Saltillo]: '$3,904'
  },
  [PillarPage.Parking]: {
    [SearchPageCity.Monterrey]: '$1,150', // a sustained 50%-off card
    [SearchPageCity.SanPedro]: '$1,700',
    [SearchPageCity.SantaCatarina]: '$1,860', // promo card in Los Treviño
    [SearchPageCity.CiudadDeMexico]: '$862', // promo card
    [SearchPageCity.Guadalajara]: '$800', // promo card
    [SearchPageCity.Toluca]: '$1,000',
    [SearchPageCity.Leon]: '$1,000'
  },
  [PillarPage.Warehouse]: {
    [SearchPageCity.Monterrey]: '$3,879', // 72.7 m² — the size rule routes it here
    [SearchPageCity.CiudadDeMexico]: '$17,250'
  },
  [PillarPage.Industrial]: {
    [SearchPageCity.Monterrey]: '$153,000' // 3,750 m², Ciudad Mitras
  }
}

/**
 * The "Desde" price for a pillar page. No city → the national floor. A city →
 * its own recorded floor, else `null`: make no claim.
 */
export function getStartingPrice(
  pillar: PillarPage,
  city: SearchPageCity | null | undefined
): string | null {
  if (!city) return PILLAR_PAGE_MAP[pillar].startingPrice
  return CITY_PRICE_FLOORS[pillar]?.[city] ?? null
}
